<template>
  <div class="cl-sidebar" :class="{'cl-sidebar--mobile-open': isMobileOpen}">
    <!-- Backdrop — click-to-close, fades with sidebar. The open/close
         trigger now lives in the topbar (see topnav.blade.php) and talks
         to this component via the `cl-sidebar:toggle` / `cl-sidebar:close`
         window events. -->
    <div
        v-if="isMobileOpen"
        class="cl-sidebar__backdrop"
        @click="closeMobile"
    ></div>

    <div class="cl-navblock">
      <div class="menu-space">
        <div class="content">
          <div class="sidebar-header">
            <strong class="sidebar-header__text" v-html="title"></strong>
            <p class="sidebar-header__date" v-html="description"></p>

            <div
                v-if="hasEditListener || showView"
                class="sidebar-header__actions"
            >
              <c-button-link
                  v-if="hasEditListener"
                  size="sm"
                  icon="fa-edit"
                  @click="$emit('edit')"
              >{{ editLabel }}
              </c-button-link>

              <c-button-link
                  v-if="showView"
                  theme="success"
                  size="sm"
                  icon="fa-arrow-up-right-from-square"
                  @click="$emit('open')"
              >View
              </c-button-link>
            </div>
          </div>
          <ul class="cl-vnavigation">
            <li v-if="!item.hidden || false" :class="`nav-checkin ${isActive(item) && 'active'}`" v-for="item in navigations">
              <a
                  :href="item.url"
                  v-if="!item.isExternal"
                  :class="{'has-submenu': item?.submenu?.length || item?.allowAddMore, 'has-submenu--open': isSubmenuOpen(item)}"
                  @click="handleItemClick(item, $event)"
              >
                <i :class="item.icon"></i>
                <span>{{ item.label }}</span>
                <span class="badge" v-if="item.count">{{ item.count }}</span>
                <span
                    v-if="item.agendaCount"
                    class="badge badge-warning"
                    data-toggle="tooltip"
                    title="Agenda Sessions"
                >+{{ item.agendaCount }}</span>
                <i
                    v-if="item?.submenu?.length || item?.allowAddMore"
                    class="fa-regular fa-chevron-down submenu-chevron"
                    aria-hidden="true"
                ></i>
              </a>
              <router-link :to="item.url" v-if="item.isExternal">
                <i :class="item.icon"></i>
                <span>{{ item.label }}</span>
                <span class="badge" v-if="item.count">{{ item.count }}</span>
                <span
                    v-if="item.agendaCount"
                    class="badge badge-warning"
                    data-toggle="tooltip"
                    title="Agenda Sessions"
                >+{{ item.agendaCount }}</span>
              </router-link>

              <!-- Submenu wrapper — grid-template-rows trick gives smooth
                   height auto → 0 transition without JS reflow. -->
              <div
                  v-if="item?.submenu?.length || item?.allowAddMore"
                  class="submenu-collapse"
                  :class="{'submenu-collapse--open': isSubmenuOpen(item)}"
              >
                <div class="submenu-collapse__inner">
                  <ul class="sub-menu nav-days-list" v-if="item?.submenu?.length">
                    <li v-for="submenu in item.submenu" :class="isActiveSubMenu(submenu) && 'active'">
                      <router-link :to="submenu.url">
                        {{ submenu.label }}
                      </router-link>
                    </li>
                  </ul>

                  <ul class="sub-menu nav-days-list" v-if="item?.allowAddMore">
                    <li>
                      <a
                          href="javascript:void(0);"
                          @click="$emit('add')"
                          class="color-success add-day"
                      >
                        <i class="fa-regular fa-plus"></i>
                        Add New Day
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
          </ul>

          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    navigations: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: 'Course'
    },
    showView: {
      type: Boolean,
      default: false
    },
    editLabel: {
      type: String,
      default: 'Edit',
    }
  },
  data() {
    return {
      pathName: window.location.pathname,
      // Tracks ONLY explicitly closed submenus (key → false).
      // Missing entries default to OPEN — matches legacy display:block
      // behavior and survives async population of `navigations` prop.
      openSubmenus: {},
      // Mobile drawer state. Closed by default; opened by hamburger FAB.
      isMobileOpen: false,
    }
  },
  computed:{
    hasEditListener(){
      return this.$listeners && this.$listeners.edit
    }
  },
  watch: {
    // Close mobile drawer whenever the route changes (router-link navigation).
    '$route'() {
      if (this.isMobileOpen) this.closeMobile()
    },
  },
  methods: {
    isActiveSubMenu(item) {
      const existInList = item.session_ids.length ? item.session_ids.some(id => id == this.$route.params.sessionId) : false;

      return this.$route.path.includes(item.url) || existInList
    },
    isActive(item) {
      if (item.isExternal) {
        return this.$route.path.includes(item.url)
      } else {
        return window.location.pathname === item.url
      }
    },
    submenuKey(item) {
      return item?.label || item?.url || ''
    },
    isSubmenuOpen(item) {
      // Default = true. Only explicit `false` means collapsed.
      return this.openSubmenus[this.submenuKey(item)] !== false
    },
    handleItemClick(item, event) {
      // Items with submenu (or `+ Add New Day`) act as toggles, not links.
      if (item?.submenu?.length || item?.allowAddMore) {
        event.preventDefault()
        const key = this.submenuKey(item)
        const currentlyOpen = this.openSubmenus[key] !== false
        this.$set(this.openSubmenus, key, !currentlyOpen)
        return
      }
      // Real navigation — auto-close mobile drawer so the new page is visible.
      if (this.isMobileOpen) {
        this.closeMobile()
      }
    },
    openMobile() {
      this.isMobileOpen = true
      // Lock body scroll while drawer is open.
      document.body.style.overflow = 'hidden'
      // Broadcast state so the topbar burger can morph into an X.
      document.body.classList.add('cl-sidebar-mobile-open')
    },
    closeMobile() {
      this.isMobileOpen = false
      document.body.style.overflow = ''
      document.body.classList.remove('cl-sidebar-mobile-open')
    },
    toggleMobile() {
      this.isMobileOpen ? this.closeMobile() : this.openMobile()
    },
    handleEscape(e) {
      if (e.key === 'Escape' && this.isMobileOpen) this.closeMobile()
    },
    // Close handler bound to the `cl-sidebar:close` window event — fired by
    // the topbar when its own drawer opens, so two menus never overlap.
    handleExternalClose() {
      if (this.isMobileOpen) this.closeMobile()
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscape)
    // The open/close trigger lives in the topbar; it toggles this drawer
    // through window events (Blade ↔ Vue bridge).
    window.addEventListener('cl-sidebar:toggle', this.toggleMobile)
    window.addEventListener('cl-sidebar:close', this.handleExternalClose)
    // Marker class — used by the topbar CSS to reveal the sidebar burger
    // only on pages that actually mount a c-sidebar.
    document.body.classList.add('has-c-sidebar')
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape)
    window.removeEventListener('cl-sidebar:toggle', this.toggleMobile)
    window.removeEventListener('cl-sidebar:close', this.handleExternalClose)
    document.body.style.overflow = ''
    document.body.classList.remove('has-c-sidebar')
    document.body.classList.remove('cl-sidebar-mobile-open')
  },
}
</script>

<style lang="scss">
/* ──────────────────────────────────────────────────────────────────
   Modern SaaS sidebar (event-module / global c-sidebar).
   Non-scoped so it can override the older global rules in
   theme.scss without bumping every selector with `:deep`.
   Brand palette: --tn-blue / --tn-blue-soft.
   ────────────────────────────────────────────────────────────────── */

$cs-blue:        #1F73C1;
$cs-blue-strong: #0E4A87;
$cs-blue-soft:   #E6F1FB;
$cs-blue-edge:   #B5D4F4;
$cs-text:        #1a1a18;
$cs-text-muted:  #5f5e5a;
$cs-text-subtle: #8a8a85;
$cs-border:      rgba(0, 0, 0, 0.08);
$cs-bg-soft:     #f8f9fa;

/* ── Container ── */
.cl-sidebar {
  background: #fff !important;
  box-shadow: none !important;
  border-right: 1px solid $cs-border;
  width: 216px !important;
  font-family: inherit;
}

.cl-sidebar .menu-space {
  width: 216px !important;
}

/* ── Header (title + date + View / Edit buttons) ── */
.cl-sidebar .sidebar-header {
  padding: 14px 16px 10px !important;
  text-align: center !important;
  border-bottom: 1px solid $cs-border;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

/* Action row — Course settings + View on one line, centered. */
.cl-sidebar .sidebar-header__actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 2px;
  width: 100%;
}

/* c-button-link wraps its <button> in a <span>; ensure that span behaves
   as an inline-flex item so the two buttons sit side-by-side. */
.cl-sidebar .sidebar-header__actions > span,
.cl-sidebar .sidebar-header__actions > a {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* Override Bootstrap btn-link padding so buttons stay compact in the row. */
.cl-sidebar .sidebar-header__actions .btn,
.cl-sidebar .sidebar-header__actions .btn-link {
  padding: 2px 4px !important;
  font-size: 12px !important;
  line-height: 1.2 !important;
  white-space: nowrap;
}

.cl-sidebar .sidebar-header__text {
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 1.35;
  color: $cs-text;
  letter-spacing: -0.005em;
  word-break: break-word;
}

.cl-sidebar .sidebar-header__date {
  font-size: 12px !important;
  color: $cs-text-muted;
  margin: 0;
  line-height: 1.3;
}

/* Status pill rendered inside the description (via v-html). Caller decides
   semantic color by passing `cs-status--published|archived|draft`. */
.cl-sidebar .sidebar-header__date .cs-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.01em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
}

.cl-sidebar .sidebar-header__date .cs-status--published {
  background: rgba(15, 110, 86, 0.10);
  color: #0F6E56;
}

.cl-sidebar .sidebar-header__date .cs-status--archived {
  background: rgba(0, 0, 0, 0.06);
  color: $cs-text-muted;
}

.cl-sidebar .sidebar-header__date .cs-status--draft {
  background: rgba(240, 169, 31, 0.14);
  color: #8a5b0a;
}

/* Action buttons in header — small chip-style links. */
.cl-sidebar .sidebar-header > .c-button-link,
.cl-sidebar .sidebar-header > a,
.cl-sidebar .sidebar-header > button {
  align-self: center;
  font-size: 12px !important;
}

/* ── Navigation list ── */
.cl-vnavigation {
  margin: 0 !important;
  padding: 8px 8px 16px !important;
  list-style: none;
}

.cl-vnavigation > li {
  margin: 0 !important;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: background 0.15s ease;
}

.cl-vnavigation > li + li {
  margin-top: 3px !important;
}

.cl-vnavigation li a {
  display: flex !important;
  align-items: center;
  gap: 8px;
  padding: 10px 12px !important;
  color: $cs-text-muted !important;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  text-decoration: none !important;
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: normal;
  background: transparent !important;
  border: 0 !important;
}

.cl-vnavigation li a:hover,
.cl-vnavigation li a:focus {
  background: $cs-bg-soft !important;
  color: $cs-text !important;
  text-decoration: none !important;
}

/* Icon — fixed slot for clean text alignment regardless of icon width.
   Brand-blue across all states; opacity steps up on hover/active so the
   icon still carries hierarchy without changing hue. */
.cl-vnavigation li a > i,
.cl-vnavigation li a > .fa {
  width: 18px;
  text-align: center;
  font-size: 14px !important;
  color: $cs-blue !important;
  opacity: 0.85;
  flex-shrink: 0;
  margin: 0 !important;
  transition: opacity 0.15s ease, color 0.15s ease;
}

/* FontAwesome duotone icons render TWO layers controlled by their own
   CSS variables — `color` alone doesn't tint them. Override both layers
   to brand blue, with the secondary slightly faded for visual depth. */
.cl-vnavigation li a > i.fa-duotone,
.cl-vnavigation li a > .fa-duotone {
  --fa-primary-color: #{$cs-blue};
  --fa-secondary-color: #{$cs-blue};
  --fa-primary-opacity: 1;
  --fa-secondary-opacity: 0.4;
}

.cl-vnavigation li a:hover > i,
.cl-vnavigation li a:hover > .fa {
  opacity: 1;
}

.cl-vnavigation li a:hover > i.fa-duotone,
.cl-vnavigation li a:hover > .fa-duotone {
  --fa-secondary-opacity: 0.55;
}

.cl-vnavigation li a > span:not(.badge) {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Badge — brand blue chip, replaces the gray Bootstrap default.
   Compact padding + tabular digits so 3-digit counts (999 / +100)
   still leave room for the item label. */
.cl-vnavigation li a .badge {
  background: $cs-blue !important;
  color: #fff !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  font-variant-numeric: tabular-nums;
  height: 18px;
  min-width: 18px;
  padding: 0 5px !important;
  border-radius: 9px !important;
  line-height: 1 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  flex-shrink: 0;
}

/* When two badges sit side by side, only the first one pushes right —
   otherwise both `margin-left: auto` would split them apart. */
.cl-vnavigation li a .badge ~ .badge {
  margin-left: 0;
}

/* Agenda count badge — keep distinct (warning) but match the chip shape. */
.cl-vnavigation li a .badge-warning {
  background: #F0A91F !important;
}

/* ── Active state — brand blue, left accent, soft tint ── */
.cl-vnavigation > li.active {
  background: $cs-blue-soft !important;
  border-left: 0 !important;
  margin-left: 0 !important;
  box-shadow: inset 3px 0 0 $cs-blue;
}

.cl-vnavigation > li.active > a {
  color: $cs-blue-strong !important;
  font-weight: 600;
  background: transparent !important;
}

.cl-vnavigation > li.active > a > i,
.cl-vnavigation > li.active > a > .fa {
  color: $cs-blue-strong !important;
  opacity: 1;
}

.cl-vnavigation > li.active > a > i.fa-duotone,
.cl-vnavigation > li.active > a > .fa-duotone {
  --fa-primary-color: #{$cs-blue-strong};
  --fa-secondary-color: #{$cs-blue-strong};
  --fa-primary-opacity: 1;
  --fa-secondary-opacity: 0.5;
}

/* Success-themed link (e.g. Add New Day) — green accent, button-ish feel. */
.cl-vnavigation li a.color-success {
  color: #0F6E56 !important;
  font-weight: 500;

  &:hover {
    background: rgba(15, 110, 86, 0.08) !important;
    color: #0F6E56 !important;
  }

  > i {
    color: inherit !important;
  }
}

/* ── Submenu collapse wrapper — grid-rows trick for smooth height auto
   animation. 0fr → 1fr animates on the compositor without JS reflow. */
.submenu-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.22s ease;
}

.submenu-collapse--open {
  grid-template-rows: 1fr;
}

.submenu-collapse__inner {
  overflow: hidden;
  min-height: 0;
  position: relative;
  z-index: 1;
}

/* Chevron indicator — rotates 180° when submenu is open. */
.cl-vnavigation li a .submenu-chevron {
  width: 12px !important;
  font-size: 10px !important;
  margin-left: 0 !important;
  color: $cs-text-subtle !important;
  opacity: 0.6;
  transition: transform 0.2s ease, opacity 0.15s ease;
  flex-shrink: 0;
}

.cl-vnavigation li a:hover .submenu-chevron {
  opacity: 0.9;
}

.cl-vnavigation li a.has-submenu--open .submenu-chevron {
  transform: rotate(180deg);
  opacity: 0.9;
}

/* Override global theme.scss `li ul { display: none }` rule. After
   restructure the ul is nested deeper, so descendant selector + !important
   keep it visible inside the collapse wrapper. */
.cl-vnavigation li ul.nav-days-list {
  display: block !important;
  padding: 4px 0 !important;
  margin: 0 !important;
  background: transparent;
  list-style: none;
}

/* Day rows — indented, smaller, click-friendly. Descendant selectors
   (no `>`) so they keep matching regardless of DOM nesting depth. */
.cl-vnavigation ul.nav-days-list li {
  border-radius: 6px;
  margin: 1px 4px !important;
  padding: 0;
  position: relative;
  z-index: 1;

  &.active {
    background: rgba(31, 115, 193, 0.08) !important;
  }

  &:hover {
    background: $cs-bg-soft !important;
  }
}

.cl-vnavigation ul.nav-days-list a {
  display: flex !important;
  align-items: center !important;
  font-size: 12.5px !important;
  padding: 7px 12px 7px 38px !important;
  color: $cs-text-muted !important;
  font-weight: 400 !important;
  text-decoration: none !important;
  background: transparent !important;
  border-radius: 6px;
  pointer-events: auto !important;
  cursor: pointer;

  &:hover {
    background: transparent !important;
    color: $cs-text !important;
  }
}

.cl-vnavigation ul.nav-days-list li.active a {
  color: $cs-blue-strong !important;
  font-weight: 600 !important;
}

/* ── Mobile drawer — slides in from the left below the topbar.
   Best-practice pattern: position:fixed + left:-100% → 0 (NOT transform,
   because transform creates a new containing block for fixed children).
   The open/close trigger lives in the topbar (topnav.blade.php) and
   toggles this drawer through the `cl-sidebar:toggle` window event. */

/* Backdrop — dims ONLY the page area to the right of the open drawer.
   It deliberately starts at left:280px (the drawer width) instead of
   covering the full viewport, so it can never overlap — and therefore
   never dim — the drawer panel itself. This sidesteps the legacy
   stacking-context conflicts that made z-index ordering unreliable. */
.cl-sidebar__backdrop {
  display: none;
  position: fixed;
  top: 60px;          /* keep topbar visible above the backdrop */
  left: 280px;        /* start where the drawer panel ends */
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1050;
  animation: cl-fade-in 0.18s ease;
}

@keyframes cl-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@media (max-width: 1023px) {
  /* Sidebar becomes a slide-in drawer below the topbar.
     z-index uses !important so it beats legacy theme.scss
     (`@media max-width:767px { .cl-sidebar { z-index:1020 } }`) regardless
     of stylesheet load order — otherwise the panel can land in a stacking
     context below the backdrop and the whole menu reads as dimmed/gray. */
  .cl-sidebar {
    display: block !important;
    position: fixed !important;
    top: 60px;
    left: -300px;
    width: 280px !important;
    height: calc(100vh - 60px);
    z-index: 1060 !important;
    background: #fff !important;
    box-shadow: 4px 0 16px rgba(0, 0, 0, 0.08);
    transition: left 0.25s ease;
    overflow-x: hidden;   /* no horizontal scroll inside the drawer */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* CRITICAL: raise the drawer content above the backdrop, which is its
     sibling inside the same stacking context. Without this the backdrop
     (z-index:1050) paints ON TOP of .cl-navblock (default z-auto), dimming
     the menu and stealing clicks. !important guards against legacy rules. */
  .cl-sidebar > .cl-navblock {
    position: relative !important;
    z-index: 1051 !important;
  }

  .cl-sidebar .menu-space {
    width: 100% !important;   /* fill the drawer, don't exceed it */
    max-width: 100% !important;
    position: static !important;
    box-sizing: border-box;
  }

  /* Override theme.scss @media (max-width: 767px) rule that hides
     .cl-vnavigation by default (legacy mobile-toggle pattern). Our
     drawer handles visibility via the parent slide-in, so the nav
     list must always render inside it. */
  .cl-sidebar .cl-vnavigation {
    display: block !important;
  }

  .cl-sidebar.cl-sidebar--mobile-open {
    left: 0;
  }

  .cl-sidebar__backdrop {
    display: block;
  }
}

/* ── Collapsed sidebar (`.sb-collapsed`) — icon-only ── */
.sb-collapsed .cl-sidebar,
.sb-collapsed .cl-sidebar .menu-space {
  width: 56px !important;
}

.sb-collapsed .cl-vnavigation li a {
  justify-content: center;
  padding: 10px 0 !important;
  gap: 0;
}

.sb-collapsed .cl-vnavigation li a > span:not(.badge) {
  display: none;
}

.sb-collapsed .cl-vnavigation li a .badge {
  position: absolute;
  top: 4px;
  right: 6px;
  margin: 0;
}

/* Icon-only mode has room for one corner badge — hide the agenda one,
   it was hidden in this mode before the markup flattening too. */
.sb-collapsed .cl-vnavigation li a .badge ~ .badge {
  display: none !important;
}
</style>
